import React, { useCallback, useEffect, useState } from 'react';
import { Edit, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { adminNewsAPI, mediaAPI } from '../../lib/api';
import {
  NEWS_CATEGORY_OPTIONS,
  SEO_LIMITS,
  buildNewsPayload,
  emptyNewsForm,
  mediaPreviewFilesFromUrl,
  newsItemToForm,
  publicationPreviewPath,
  slugifyNewsTitle,
  suggestSeoFromForm,
} from '../../lib/adminNewsForm';
import { getCategoryMeta } from '../../lib/newsPublications';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import MediaManager from '../MediaManager';
import RichTextEditor from '../RichTextEditor';

const uploadNewsImage = async (file) => mediaAPI.upload(file, 'news');

const SeoCounter = ({ value, limit }) => (
  <span className={`text-xs ${value.length > limit ? 'text-destructive' : 'text-muted-foreground'}`}>
    {value.length}/{limit}
  </span>
);

const AdminNewsTab = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyNewsForm());
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminNewsAPI.getAll();
      setItems(data || []);
    } catch (err) {
      toast({ title: 'Ошибка загрузки', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resetForm = () => {
    setEditing(null);
    setForm(emptyNewsForm());
  };

  const handleTitleChange = (title) => {
    setForm((prev) => ({
      ...prev,
      title,
      slug: prev.slug || slugifyNewsTitle(title),
      meta_title: prev.meta_title || title.slice(0, SEO_LIMITS.metaTitle),
    }));
  };

  const applySeoSuggestions = () => {
    const suggested = suggestSeoFromForm(form);
    setForm((prev) => ({ ...prev, ...suggested }));
    toast({ title: 'SEO/GEO поля обновлены из заголовка и описания' });
  };

  const handleSave = async () => {
    if (!(form.title || '').trim()) {
      toast({ title: 'Укажите заголовок', variant: 'destructive' });
      return;
    }
    if (!(form.slug || '').trim()) {
      toast({ title: 'Укажите slug', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = buildNewsPayload(form);
      if (editing?.id) {
        await adminNewsAPI.update(editing.id, payload);
        toast({ title: 'Публикация обновлена' });
      } else {
        await adminNewsAPI.create(payload);
        toast({ title: 'Публикация создана' });
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast({ title: 'Ошибка сохранения', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editing ? 'Редактирование публикации' : 'Новая публикация'}</CardTitle>
          <CardDescription>
            SEO: уникальный title и description. GEO: краткий ответ в начале (geo_summary), key takeaways и FAQ для AI-цитирования.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Заголовок (H1)</Label>
              <Input value={form.title} onChange={(e) => handleTitleChange(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Slug (URL)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                placeholder="partnerstvo-just-ai"
              />
              {form.slug && (
                <p className="text-xs text-muted-foreground">
                  Публичный URL: {publicationPreviewPath(form.slug)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Категория</Label>
              <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NEWS_CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Дата для отображения</Label>
              <Input
                value={form.display_date}
                onChange={(e) => setForm((p) => ({ ...p, display_date: e.target.value }))}
                placeholder="Март 2026"
              />
            </div>
            <div className="space-y-2">
              <Label>Дата публикации (ISO)</Label>
              <Input
                type="date"
                value={form.published_at}
                onChange={(e) => setForm((p) => ({ ...p, published_at: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Автор</Label>
              <Input value={form.author} onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Порядок сортировки</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Краткое описание (карточка, meta fallback)</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Превью (карточка)</Label>
              <MediaManager
                files={mediaPreviewFilesFromUrl(form.preview_image)}
                onFilesChange={(files) => setForm((p) => ({ ...p, preview_image: files[0]?.url || '' }))}
                maxFiles={1}
                acceptedFileTypes={['image/*']}
                onUpload={uploadNewsImage}
                uploading={uploadingPreview}
                onUploadingChange={setUploadingPreview}
              />
            </div>
            <div className="space-y-2">
              <Label>Основное изображение (статья)</Label>
              <MediaManager
                files={mediaPreviewFilesFromUrl(form.main_image)}
                onFilesChange={(files) => setForm((p) => ({ ...p, main_image: files[0]?.url || '' }))}
                maxFiles={1}
                acceptedFileTypes={['image/*']}
                onUpload={uploadNewsImage}
                uploading={uploadingMain}
                onUploadingChange={setUploadingMain}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Полный текст (HTML)</Label>
            <RichTextEditor
              value={form.full_text || ''}
              onChange={(value) => setForm((p) => ({ ...p, full_text: value }))}
              placeholder="Текст публикации..."
              onUploadImage={uploadNewsImage}
            />
          </div>

          <Card className="border-accent/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-lg">SEO</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={applySeoSuggestions}>
                  Заполнить из заголовка
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Meta title</Label>
                  <SeoCounter value={form.meta_title || ''} limit={SEO_LIMITS.metaTitle} />
                </div>
                <Input
                  value={form.meta_title}
                  onChange={(e) => setForm((p) => ({ ...p, meta_title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Meta description</Label>
                  <SeoCounter value={form.meta_description || ''} limit={SEO_LIMITS.metaDescription} />
                </div>
                <Textarea
                  rows={3}
                  value={form.meta_description}
                  onChange={(e) => setForm((p) => ({ ...p, meta_description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Keywords (через запятую)</Label>
                <Input
                  value={form.meta_keywords}
                  onChange={(e) => setForm((p) => ({ ...p, meta_keywords: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Canonical path</Label>
                <Input
                  value={form.canonical_path}
                  onChange={(e) => setForm((p) => ({ ...p, canonical_path: e.target.value }))}
                  placeholder={form.slug ? publicationPreviewPath(form.slug) : ''}
                />
              </div>
              <div className="space-y-2">
                <Label>OG image URL (если отличается от основной)</Label>
                <Input
                  value={form.og_image}
                  onChange={(e) => setForm((p) => ({ ...p, og_image: e.target.value }))}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-accent/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">GEO — оптимизация для AI-цитирования</CardTitle>
              <CardDescription>
                Краткий ответ в начале, выводы и FAQ помогают попадать в ответы ChatGPT, Perplexity и AI Overviews.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>GEO summary (answer-first, 1–2 предложения)</Label>
                  <SeoCounter value={form.geo_summary || ''} limit={SEO_LIMITS.geoSummary} />
                </div>
                <Textarea
                  rows={3}
                  value={form.geo_summary}
                  onChange={(e) => setForm((p) => ({ ...p, geo_summary: e.target.value }))}
                  placeholder="Прямой ответ: что произошло и зачем это важно бизнесу."
                />
              </div>
              <div className="space-y-2">
                <Label>Key takeaways</Label>
                {form.key_takeaways.map((item, index) => (
                  <div key={`takeaway-${index}`} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => {
                        const next = [...form.key_takeaways];
                        next[index] = e.target.value;
                        setForm((p) => ({ ...p, key_takeaways: next }));
                      }}
                      placeholder={`Вывод ${index + 1}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const next = form.key_takeaways.filter((_, i) => i !== index);
                        setForm((p) => ({ ...p, key_takeaways: next.length ? next : [''] }));
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setForm((p) => ({ ...p, key_takeaways: [...p.key_takeaways, ''] }))}
                >
                  <Plus className="h-4 w-4 mr-1" /> Добавить вывод
                </Button>
              </div>
              <div className="space-y-2">
                <Label>FAQ (для schema FAQPage)</Label>
                {form.faq_items.map((item, index) => (
                  <div key={`faq-${index}`} className="grid gap-2 rounded-lg border p-3">
                    <Input
                      value={item.question}
                      onChange={(e) => {
                        const next = [...form.faq_items];
                        next[index] = { ...next[index], question: e.target.value };
                        setForm((p) => ({ ...p, faq_items: next }));
                      }}
                      placeholder="Вопрос"
                    />
                    <Textarea
                      rows={2}
                      value={item.answer}
                      onChange={(e) => {
                        const next = [...form.faq_items];
                        next[index] = { ...next[index], answer: e.target.value };
                        setForm((p) => ({ ...p, faq_items: next }));
                      }}
                      placeholder="Краткий ответ"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-fit"
                      onClick={() => {
                        const next = form.faq_items.filter((_, i) => i !== index);
                        setForm((p) => ({
                          ...p,
                          faq_items: next.length ? next : [{ question: '', answer: '' }],
                        }));
                      }}
                    >
                      Удалить вопрос
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setForm((p) => ({
                    ...p,
                    faq_items: [...p.faq_items, { question: '', answer: '' }],
                  }))}
                >
                  <Plus className="h-4 w-4 mr-1" /> Добавить FAQ
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Внешняя ссылка (если публикация ведёт не на сайт)</Label>
            <Input
              value={form.external_href}
              onChange={(e) => setForm((p) => ({ ...p, external_href: e.target.value }))}
              placeholder="https://..."
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="news-published"
                checked={form.is_published}
                onCheckedChange={(checked) => setForm((p) => ({ ...p, is_published: !!checked }))}
              />
              <Label htmlFor="news-published">Опубликовано</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="news-featured"
                checked={form.is_featured}
                onCheckedChange={(checked) => setForm((p) => ({ ...p, is_featured: !!checked }))}
              />
              <Label htmlFor="news-featured">На главной (карусель)</Label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Сохранение...' : editing ? 'Обновить' : 'Создать'}
            </Button>
            {editing && (
              <Button variant="outline" onClick={resetForm}>Отмена</Button>
            )}
            {form.slug && (
              <Button variant="outline" asChild>
                <a href={publicationPreviewPath(form.slug)} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" /> Предпросмотр
                </a>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Список публикаций</CardTitle>
          <CardDescription>{loading ? 'Загрузка...' : `${items.length} записей`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Заголовок</TableHead>
                <TableHead>Категория</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="max-w-xs truncate">{item.title}</TableCell>
                  <TableCell>{getCategoryMeta(item.category).label}</TableCell>
                  <TableCell className="font-mono text-xs">{item.slug}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {item.is_published ? <Badge>Опубл.</Badge> : <Badge variant="secondary">Черновик</Badge>}
                      {item.is_featured && <Badge variant="outline">Главная</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditing(item);
                          setForm(newsItemToForm(item));
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm('Удалить публикацию?')) return;
                          try {
                            await adminNewsAPI.remove(item.id);
                            toast({ title: 'Удалено' });
                            if (editing?.id === item.id) resetForm();
                            await loadItems();
                          } catch (err) {
                            toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNewsTab;
